import { useNavigate } from "@tanstack/react-router";
import { CodeIcon, ExternalIcon, LockIcon, MoreIcon } from "./icons";
import { ThemeToggle } from "./ThemeToggle";
import { Menu } from "./ui/Menu";
import { useLock, useSession } from "@/api/queries";
import { cn } from "@/lib/cn";

/**
 * The viewer's own three controls — theme, API docs, Lock (backlog item 13).
 *
 * These used to be a 64px bar across the top of every page whose only other
 * content was a logo the sidebar was already showing. They belong beside the
 * status dot and the version, and the backlog asked for exactly that.
 *
 * They are a menu rather than a row because the footer they moved into has
 * **two lives**. Expanded it is 256px of usable width already carrying two
 * lines; the three controls laid side by side are ~240px before gaps, which
 * fits the way a crowbar fits. Collapsed it is a 40px rail, and 94px of theme
 * toggle does not fit at all. One trigger answers both — and `Menu` is on the
 * top layer, so the `overflow-hidden` on the `aside` cannot clip it (item 12).
 *
 * The theme control inside is `ThemeToggle` itself, unmodified. Its three
 * states — explicit light, explicit dark, and the `system` default that
 * follows `prefers-color-scheme` — are all load-bearing, and the obvious way
 * to save horizontal space (fold it to a two-way switch) silently deletes
 * "follow the OS" for everyone who had it. Three targets, one implementation,
 * shared with the unlock page.
 */
export function ViewerMenu({ align = "end" }: { align?: "start" | "end" }) {
  const lock = useLock();
  const navigate = useNavigate();
  const { data: session } = useSession();

  const item = cn(
    "type-support text-ink hover:bg-hover rounded-inner motion-fast",
    "flex w-full items-center gap-2 px-2 py-1.5 text-start",
  );

  return (
    <Menu
      label="Preferences"
      align={align}
      className="w-52"
      trigger={(props) => (
        <button
          type="button"
          {...props}
          title="Preferences"
          className={cn(
            "text-ink-disabled hover:bg-hover hover:text-ink rounded-element motion-fast",
            "flex size-8 shrink-0 items-center justify-center transition-colors",
          )}
        >
          <MoreIcon className="size-4" />
          <span className="sr-only">Preferences</span>
        </button>
      )}
    >
      {(close) => (
        <>
          {/* Who this session speaks for (ACC-3, ACC-2). A key session has a
              tier but no name, and says only the tier. */}
          {session?.authenticated ? (
            <>
              <div className="px-2 py-1.5">
                {session.user ? (
                  <p className="type-label text-ink truncate">{session.user.display_name}</p>
                ) : null}
                <p className="type-support text-ink-secondary truncate">
                  {session.user ? `${session.user.username} · ` : "Access key · "}
                  {session.role === "admin" ? "Admin" : "Analyst"}
                </p>
              </div>
              <div className="border-border my-1 border-t" />
            </>
          ) : null}
          <p className="type-caption text-ink-disabled px-2 py-1">Theme</p>
          <div className="px-2 pb-1">
            <ThemeToggle />
          </div>

          <div className="border-border my-1 border-t" />

          {/* Not a route: this is FastAPI's own Swagger UI, so it keeps the
              trailing mark that says it leaves the app rather than reading
              like another page of it. */}
          <a
            href="/api/docs"
            target="_blank"
            rel="noreferrer"
            role="menuitem"
            onClick={close}
            className={item}
          >
            <CodeIcon className="size-3.5 shrink-0" />
            <span className="flex-1">API docs</span>
            <ExternalIcon className="text-ink-disabled size-3 shrink-0" />
          </a>

          {/* A bearer-token client has no cookie session to end, and a Lock
              that cannot work is worse than no Lock. `mutate` with an
              `onSuccess` navigate, so a failure still raises a notice (item
              11) rather than looking like a click that did nothing. */}
          {session?.method === "cookie" ? (
            <button
              type="button"
              role="menuitem"
              className={item}
              onClick={() => {
                close();
                lock.mutate(undefined, {
                  onSuccess: () => void navigate({ to: "/unlock" }),
                });
              }}
            >
              <LockIcon className="size-3.5 shrink-0" />
              <span className="flex-1">{session.user ? "Sign out" : "Lock"}</span>
            </button>
          ) : null}
        </>
      )}
    </Menu>
  );
}
